import { useState, useRef, useEffect } from 'react';

interface PrintReadyModalProps {
  onClose: () => void;
  darkMode?: boolean;
  // Support both calling conventions
  selectedStyle?: string;
  floorPlanConfig?: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    unit?: string;
    kitchenType?: string;
    extras?: string[];
  };
  // Legacy support
  isOpen?: boolean;
  floorPlanData?: {
    style: string;
    bedrooms: number;
    bathrooms: number;
    area: number;
    kitchenType: string;
    extras: string[];
  };
}

type PaperSize = 'A4' | 'A3';
type Orientation = 'portrait' | 'landscape';

export default function PrintReadyModal({
  onClose,
  darkMode = false,
  selectedStyle,
  floorPlanConfig,
  isOpen,
  floorPlanData,
}: PrintReadyModalProps) {
  // Normalize data from either calling convention
  const normalizedData = floorPlanData ?? {
    style: selectedStyle ?? '',
    bedrooms: floorPlanConfig?.bedrooms ?? 3,
    bathrooms: floorPlanConfig?.bathrooms ?? 2,
    area: floorPlanConfig?.area ?? 2000,
    kitchenType: floorPlanConfig?.kitchenType ?? 'Standard',
    extras: floorPlanConfig?.extras ?? [],
  };

  const [paperSize, setPaperSize] = useState<PaperSize>('A4');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [showPreview, setShowPreview] = useState(true);
  const printContentRef = useRef<HTMLDivElement>(null);

  // ✅ useEffect MUST be before any early return
  useEffect(() => {
    if (isOpen === false) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // If isOpen is explicitly false, don't render
  if (isOpen === false) return null;

  const getRoomBreakdown = () => {
    const { bedrooms, bathrooms, area, style, kitchenType, extras } = normalizedData;
    const rooms = [];

    rooms.push({
      name: 'Living Room',
      size: Math.round(area * 0.25),
      description: `Spacious living area with ${style.toLowerCase()} design elements, perfect for entertaining and relaxation.`
    });

    rooms.push({
      name: 'Master Bedroom',
      size: Math.round(area * 0.18),
      description: `Primary bedroom with en-suite bathroom, walk-in closet, and ${style.toLowerCase()} finishes.`
    });

    for (let i = 2; i <= bedrooms; i++) {
      rooms.push({
        name: `Bedroom ${i}`,
        size: Math.round(area * 0.12),
        description: `Comfortable bedroom with ample closet space and natural lighting.`
      });
    }

    rooms.push({
      name: 'Kitchen',
      size: Math.round(area * 0.15),
      description: `${kitchenType} kitchen with modern appliances and ${style.toLowerCase()} cabinetry.`
    });

    rooms.push({
      name: 'Dining Room',
      size: Math.round(area * 0.12),
      description: `Elegant dining space adjacent to the kitchen, ideal for family meals.`
    });

    for (let i = 1; i <= bathrooms; i++) {
      rooms.push({
        name: i === 1 ? 'Master Bathroom' : `Bathroom ${i}`,
        size: Math.round(area * 0.06),
        description: `${i === 1 ? 'Luxurious' : 'Modern'} bathroom with ${style.toLowerCase()} fixtures and finishes.`
      });
    }

    extras.forEach(extra => {
      let size = 0;
      let description = '';
      switch (extra) {
        case 'Home Office':
          size = Math.round(area * 0.08);
          description = 'Dedicated workspace with built-in shelving and natural light.';
          break;
        case 'Garage':
          size = Math.round(area * 0.20);
          description = 'Two-car garage with storage space and direct home access.';
          break;
        case 'Balcony':
          size = Math.round(area * 0.05);
          description = 'Outdoor balcony space with scenic views.';
          break;
        case 'Skylight':
          size = 0;
          description = 'Natural skylights throughout for enhanced lighting.';
          break;
        case 'Walk-in Closet':
          size = Math.round(area * 0.04);
          description = 'Spacious walk-in closet with custom organization systems.';
          break;
        case 'Laundry Room':
          size = Math.round(area * 0.05);
          description = 'Dedicated laundry room with washer, dryer, and storage.';
          break;
      }
      if (size > 0 || extra === 'Skylight') {
        rooms.push({ name: extra, size, description });
      }
    });

    return rooms;
  };

  const handlePrintNow = () => {
    const printStyles = `
      @media print {
        @page { size: ${paperSize} ${orientation}; margin: 15mm 12mm 15mm 12mm; }
        body * { visibility: hidden; }
        #print-content, #print-content * { visibility: visible; }
        #print-content { position: absolute; left: 0; top: 0; width: 100%; background: white !important; color: black !important; }
        .print-page-break { page-break-before: always; }
        .print-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 2px solid #14B8A6; margin-bottom: 20px; }
        .print-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 10pt; padding-top: 10px; border-top: 1px solid #e5e7eb; }
        .print-section { margin-bottom: 25px; }
        .print-section-title { font-size: 16pt; font-weight: bold; color: #14B8A6; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; }
        .print-room-item { margin-bottom: 15px; padding: 10px; border: 1px solid #e5e7eb; border-radius: 4px; }
        .print-room-name { font-size: 12pt; font-weight: bold; margin-bottom: 4px; }
        .print-room-size { font-size: 10pt; color: #6b7280; margin-bottom: 6px; }
        .print-room-desc { font-size: 10pt; line-height: 1.4; }
        .print-summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-bottom: 20px; }
        .print-summary-item { padding: 12px; border: 1px solid #e5e7eb; border-radius: 4px; }
        .print-summary-label { font-size: 10pt; color: #6b7280; margin-bottom: 4px; }
        .print-summary-value { font-size: 14pt; font-weight: bold; }
      }
    `;
    const styleSheet = document.createElement('style');
    styleSheet.textContent = printStyles;
    document.head.appendChild(styleSheet);
    window.print();
    setTimeout(() => { document.head.removeChild(styleSheet); }, 1000);
  };

  const handleDownloadPDF = async () => {
    const { jsPDF } = await import('jspdf');
    const pageWidth = paperSize === 'A4' ? (orientation === 'portrait' ? 210 : 297) : (orientation === 'portrait' ? 297 : 420);
    const pageHeight = paperSize === 'A4' ? (orientation === 'portrait' ? 297 : 210) : (orientation === 'portrait' ? 420 : 297);
    const doc = new jsPDF({ orientation, unit: 'mm', format: paperSize.toLowerCase() });
    const marginLeft = 12, marginRight = 12, marginTop = 15, marginBottom = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    let yPos = marginTop;

    const addHeader = () => {
      doc.setFontSize(18); doc.setTextColor(20, 184, 166);
      doc.text('Master Architect', marginLeft, yPos);
      doc.setFontSize(10); doc.setTextColor(107, 114, 128);
      doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageWidth - marginRight, yPos, { align: 'right' });
      yPos += 8; doc.setDrawColor(20, 184, 166); doc.setLineWidth(0.5);
      doc.line(marginLeft, yPos, pageWidth - marginRight, yPos); yPos += 10;
    };

    const addFooter = (pageNum: number, totalPages: number) => {
      doc.setFontSize(9); doc.setTextColor(107, 114, 128);
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    };

    const checkNewPage = (requiredSpace: number) => {
      if (yPos + requiredSpace > pageHeight - marginBottom - 15) {
        doc.addPage(); yPos = marginTop; addHeader(); return true;
      }
      return false;
    };

    addHeader();
    doc.setFontSize(14); doc.setTextColor(20, 184, 166);
    doc.text('Floor Plan Summary', marginLeft, yPos); yPos += 10;

    const summaryItems = [
      { label: 'Architectural Style', value: normalizedData.style },
      { label: 'Total Area', value: `${normalizedData.area} sq ft` },
      { label: 'Bedrooms', value: normalizedData.bedrooms.toString() },
      { label: 'Bathrooms', value: normalizedData.bathrooms.toString() },
      { label: 'Kitchen Type', value: normalizedData.kitchenType },
      { label: 'Special Features', value: normalizedData.extras.length.toString() },
    ];

    doc.setFontSize(10);
    const itemsPerRow = 2, itemWidth = contentWidth / itemsPerRow, itemHeight = 20;
    summaryItems.forEach((item, index) => {
      const col = index % itemsPerRow, row = Math.floor(index / itemsPerRow);
      const x = marginLeft + col * itemWidth, y = yPos + row * itemHeight;
      doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.rect(x + 1, y, itemWidth - 2, itemHeight - 2);
      doc.setTextColor(107, 114, 128); doc.setFontSize(9); doc.text(item.label, x + 3, y + 6);
      doc.setTextColor(0, 0, 0); doc.setFontSize(11); doc.text(item.value, x + 3, y + 13);
    });
    yPos += Math.ceil(summaryItems.length / itemsPerRow) * itemHeight + 10;

    if (normalizedData.extras.length > 0) {
      checkNewPage(30);
      doc.setFontSize(12); doc.setTextColor(20, 184, 166); doc.text('Special Features', marginLeft, yPos); yPos += 8;
      doc.setFontSize(10); doc.setTextColor(0, 0, 0);
      normalizedData.extras.forEach(extra => { checkNewPage(8); doc.text(`• ${extra}`, marginLeft + 3, yPos); yPos += 6; });
      yPos += 5;
    }

    addFooter(1, 2);
    doc.addPage(); yPos = marginTop; addHeader();
    doc.setFontSize(14); doc.setTextColor(20, 184, 166); doc.text('Room-by-Room Breakdown', marginLeft, yPos); yPos += 10;

    const rooms = getRoomBreakdown();
    rooms.forEach(room => {
      const roomHeight = room.size > 0 ? 25 : 20;
      checkNewPage(roomHeight);
      doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.3); doc.rect(marginLeft, yPos, contentWidth, roomHeight - 3);
      doc.setFontSize(11); doc.setTextColor(0, 0, 0); doc.text(room.name, marginLeft + 3, yPos + 6);
      if (room.size > 0) { doc.setFontSize(9); doc.setTextColor(107, 114, 128); doc.text(`${room.size} sq ft`, marginLeft + 3, yPos + 11); }
      doc.setFontSize(9); doc.setTextColor(75, 85, 99);
      const descLines = doc.splitTextToSize(room.description, contentWidth - 6);
      doc.text(descLines, marginLeft + 3, yPos + (room.size > 0 ? 16 : 11));
      yPos += roomHeight;
    });

    addFooter(2, 2);
    doc.save(`floor-plan-${paperSize}-${orientation}.pdf`);
  };

  const rooms = getRoomBreakdown();
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const cardBg = darkMode ? 'bg-gray-900' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${cardBg} rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${borderColor} flex items-center justify-between`}>
          <div>
            <h2 className={`text-2xl font-bold ${textPrimary}`}>Print-Ready Mode</h2>
            <p className={`text-sm ${textSecondary} mt-1`}>Format your floor plan for professional printing</p>
          </div>
          <button
            onClick={onClose}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Settings Panel */}
            <div className="space-y-6">
              {/* Paper Size */}
              <div>
                <label className={`block text-sm font-semibold ${textPrimary} mb-3`}>Paper Size</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['A4', 'A3'] as PaperSize[]).map(size => (
                    <button
                      key={size}
                      onClick={() => setPaperSize(size)}
                      className={`px-4 py-3 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                        paperSize === size
                          ? `border-teal-500 ${darkMode ? 'bg-teal-900/20 text-teal-400' : 'bg-teal-50 text-teal-700'}`
                          : `${borderColor} ${darkMode ? 'hover:border-gray-600' : 'hover:border-gray-300'} ${textPrimary}`
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold">{size}</div>
                        <div className={`text-xs mt-1 ${textSecondary}`}>{size === 'A4' ? '210 × 297 mm' : '297 × 420 mm'}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Orientation */}
              <div>
                <label className={`block text-sm font-semibold ${textPrimary} mb-3`}>Orientation</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['portrait', 'landscape'] as Orientation[]).map(ori => (
                    <button
                      key={ori}
                      onClick={() => setOrientation(ori)}
                      className={`px-4 py-3 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                        orientation === ori
                          ? `border-teal-500 ${darkMode ? 'bg-teal-900/20 text-teal-400' : 'bg-teal-50 text-teal-700'}`
                          : `${borderColor} ${darkMode ? 'hover:border-gray-600' : 'hover:border-gray-300'} ${textPrimary}`
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-file-text-line text-lg" style={ori === 'landscape' ? { transform: 'rotate(90deg)' } : {}}></i>
                        <span className="font-semibold capitalize">{ori}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview Toggle */}
              <div>
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowPreview(v => !v)}>
                  <span className={`text-sm font-semibold ${textPrimary}`}>Show Preview</span>
                  <div className={`relative w-12 h-6 rounded-full transition-colors ${showPreview ? 'bg-teal-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${showPreview ? 'translate-x-6' : ''}`}></div>
                  </div>
                </div>
              </div>

              {/* Print Info */}
              <div className={`${darkMode ? 'bg-teal-900/20 border-teal-800' : 'bg-teal-50 border-teal-200'} border rounded-lg p-4`}>
                <div className="flex gap-3">
                  <i className={`ri-information-line text-lg flex-shrink-0 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}></i>
                  <div className={`text-sm ${darkMode ? 'text-teal-300' : 'text-teal-800'}`}>
                    <p className="font-semibold mb-1">Print Settings</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Margins: 15mm top/bottom, 12mm sides</li>
                      <li>• Auto page breaks between sections</li>
                      <li>• Headers and page numbers included</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handlePrintNow}
                  className="w-full px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-printer-line text-lg"></i>Print Now
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className={`w-full px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-white ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-900 hover:bg-gray-800'}`}
                >
                  <i className="ri-download-line text-lg"></i>Download Print PDF
                </button>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-2">
              {showPreview ? (
                <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg p-6 h-full overflow-y-auto`}>
                  <div
                    ref={printContentRef}
                    id="print-content"
                    className={`bg-white mx-auto shadow-lg ${
                      paperSize === 'A4'
                        ? orientation === 'portrait' ? 'w-[210mm] min-h-[297mm]' : 'w-[297mm] min-h-[210mm]'
                        : orientation === 'portrait' ? 'w-[297mm] min-h-[420mm]' : 'w-[420mm] min-h-[297mm]'
                    }`}
                    style={{ padding: '15mm 12mm' }}
                  >
                    {/* Page 1 Header */}
                    <div className="print-header">
                      <div>
                        <h1 className="text-2xl font-bold text-teal-600">Master Architect</h1>
                        <p className="text-sm text-gray-600 mt-1">Floor Plan Design</p>
                      </div>
                      <div className="text-right text-sm text-gray-600">{currentDate}</div>
                    </div>

                    <div className="print-section">
                      <h2 className="print-section-title">Floor Plan Summary</h2>
                      <div className="print-summary-grid">
                        {[
                          { label: 'Architectural Style', value: normalizedData.style || '—' },
                          { label: 'Total Area', value: `${normalizedData.area} sq ft` },
                          { label: 'Bedrooms', value: String(normalizedData.bedrooms) },
                          { label: 'Bathrooms', value: String(normalizedData.bathrooms) },
                          { label: 'Kitchen Type', value: normalizedData.kitchenType },
                          { label: 'Special Features', value: String(normalizedData.extras.length) },
                        ].map(item => (
                          <div key={item.label} className="print-summary-item">
                            <div className="print-summary-label">{item.label}</div>
                            <div className="print-summary-value">{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {normalizedData.extras.length > 0 && (
                      <div className="print-section">
                        <h2 className="print-section-title">Special Features</h2>
                        <ul className="space-y-2">
                          {normalizedData.extras.map((extra, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm">
                              <i className="ri-checkbox-circle-fill text-teal-500"></i>{extra}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="print-footer">Page 1 of 2</div>
                    <div className="print-page-break"></div>

                    {/* Page 2 Header */}
                    <div className="print-header">
                      <div>
                        <h1 className="text-2xl font-bold text-teal-600">Master Architect</h1>
                        <p className="text-sm text-gray-600 mt-1">Floor Plan Design</p>
                      </div>
                      <div className="text-right text-sm text-gray-600">{currentDate}</div>
                    </div>

                    <div className="print-section">
                      <h2 className="print-section-title">Room-by-Room Breakdown</h2>
                      <div className="space-y-4">
                        {rooms.map((room, index) => (
                          <div key={index} className="print-room-item">
                            <div className="print-room-name">{room.name}</div>
                            {room.size > 0 && <div className="print-room-size">{room.size} sq ft</div>}
                            <div className="print-room-desc">{room.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="print-footer">Page 2 of 2</div>
                  </div>
                </div>
              ) : (
                <div className={`flex items-center justify-center h-full ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg`}>
                  <div className={`text-center ${textSecondary}`}>
                    <i className="ri-eye-off-line text-5xl mb-3 block"></i>
                    <p className="text-sm">Preview hidden</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
